// ReportReviewChecklist.jsx
import React, { useState,useEffect, useRef } from 'react';
import './ReportReviewChecklist.css';

// Import individual icons from lucide-react
import { 
  Check, 
  FileText, 
  ClipboardList, 
  AlertCircle, 
  Send, 
  Download,
  CheckCircle,
  Circle,
  Printer,
  Building
} from 'lucide-react';

// Import jsPDF and html2canvas for PDF generation
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const checklistSections = [
  {
    title: "1. Basic Details",
    icon: <ClipboardList size={20} />,
    items: [
      "Vendor name",
      "PO number / Item description",
      "Inspection date & location",
      "Inspector name",
      "ITP reference"
    ]
  },
  {
    title: "2. Document Check",
    icon: <FileText size={20} />,
    items: [
      "Approved drawings used",
      "MTC attached",
      "Calibration certificates",
      "NDT/Welding documents (if applicable)"
    ]
  },
  {
    title: "3. Inspection Results",
    icon: <AlertCircle size={20} />,
    items: [
      "Material identification & dimensions",
      "Visual inspection",
      "NDT / Testing reports attached",
      "Painting/Coating check (if applicable)",
      "Photographs attached"
    ]
  },
  {
    title: "4. Compliance",
    icon: <Check size={20} />,
    items: [
      "Meets PO/specifications",
      "NCR/Deviation noted (if any)",
      "Final status: Accepted / Rejected"
    ]
  },
  {
    title: "5. Closing",
    icon: <Send size={20} />,
    items: [
      "Inspector signature & stamp",
      "Vendor signature",
      "All attachments included"
    ]
  }
];

const statusOptions = [
  { id: "accepted", label: "Accepted", value: "accepted" },
  { id: "rejected", label: "Rejected", value: "rejected" },
  { id: "pending", label: "Pending Review", value: "pending" }
];

const ReportReviewChecklist = () => {
  useEffect(() => {
      document.title = "Checklist | Qnexa Services — Engineering & Inspection Specialists";
    }, []);
  const [checkedItems, setCheckedItems] = useState({});
  const [completedAll, setCompletedAll] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedItems, setSelectedItems] = useState({});
  const [reportStatus, setReportStatus] = useState("pending");
  const [inspectorName, setInspectorName] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [companyName, setCompanyName] = useState("");
  
  const componentRef = useRef();

  const handleCheck = (sectionIndex, itemIndex) => {
    const key = `${sectionIndex}-${itemIndex}`;
    
    const newCheckedItems = {
      ...checkedItems,
      [key]: !checkedItems[key]
    };
    
    setCheckedItems(newCheckedItems);
    
    const totalItems = checklistSections.reduce((sum, section) => sum + section.items.length, 0);
    const checkedCount = Object.values(newCheckedItems).filter(Boolean).length;
    const newProgress = Math.round((checkedCount / totalItems) * 100);
    setProgress(newProgress);
    setCompletedAll(newProgress === 100);
    
    setSelectedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    
    setTimeout(() => {
      setSelectedItems(prev => ({
        ...prev,
        [key]: false
      }));
    }, 300);
  };

  const handleCheckboxChange = (e, sectionIndex, itemIndex) => {
    e.stopPropagation();
    handleCheck(sectionIndex, itemIndex);
  };

  const handleKeyPress = (e, sectionIndex, itemIndex) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCheck(sectionIndex, itemIndex);
    }
  };

  const handleStatusChange = (e) => {
    setReportStatus(e.target.value);
  };

  const resetChecklist = () => {
    setCheckedItems({});
    setProgress(0);
    setCompletedAll(false);
    setCompanyName("");
    setReportStatus("pending");
    setInspectorName("");
    setInspectionDate("");
  };

  const selectAll = () => {
    const allChecked = {};
    checklistSections.forEach((section, sectionIndex) => {
      section.items.forEach((item, itemIndex) => {
        allChecked[`${sectionIndex}-${itemIndex}`] = true;
      });
    });
    setCheckedItems(allChecked);
    setProgress(100);
    setCompletedAll(true);
  };

  const clearAll = () => {
    setCheckedItems({});
    setProgress(0);
    setCompletedAll(false);
  };

  const getStatusColor = () => {
    switch(reportStatus) {
      case 'accepted': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#64748b';
    }
  };

  const getStatusLabel = () => {
    switch(reportStatus) {
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending Review';
      default: return 'Not Set';
    }
  };

  // Function to open print dialog
  const printReport = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get current date
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const formattedTime = currentDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // Count completed items
    const totalItems = checklistSections.reduce((sum, section) => sum + section.items.length, 0);
    const completedCount = Object.values(checkedItems).filter(Boolean).length;
    
    // Generate HTML content for print
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TPI Inspection Report Checklist</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 40px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
          }
          .header h1 {
            color: #1e293b;
            font-size: 28px;
            margin: 0;
          }
          .header h2 {
            color: #64748b;
            font-size: 18px;
            margin: 10px 0;
          }
          .meta-info {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;
            background: #f8fafc;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
          }
          .meta-item {
            font-size: 14px;
          }
          .meta-item strong {
            color: #1e293b;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            color: white;
            font-size: 12px;
            font-weight: bold;
            margin-left: 8px;
          }
          .checklist-section {
            margin: 30px 0;
            page-break-inside: avoid;
          }
          .section-title {
            background: linear-gradient(135deg, #2563eb, #60a5fa);
            color: white;
            padding: 15px;
            border-radius: 8px;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
          }
          .checklist-items {
            list-style: none;
            padding: 0;
          }
          .checklist-item {
            display: flex;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid #e2e8f0;
          }
          .checklist-item:last-child {
            border-bottom: none;
          }
          .checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #ddd;
            border-radius: 4px;
            margin-right: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .checked .checkbox {
            background-color: #10b981;
            border-color: #10b981;
          }
          .checked .checkbox::after {
            content: "✓";
            color: white;
            font-weight: bold;
          }
          .item-text {
            flex: 1;
          }
          .checked .item-text {
            text-decoration: line-through;
            color: #64748b;
          }
          .summary {
            background: #f8fafc;
            padding: 25px;
            border-radius: 8px;
            margin: 30px 0;
          }
          .summary h3 {
            color: #1e293b;
            margin-top: 0;
          }
          .stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 20px;
            margin: 20px 0;
          }
          .stat {
            text-align: center;
            padding: 15px;
            background: white;
            border-radius: 8px;
          }
          .stat-value {
            font-size: 32px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 5px;
          }
          .stat-label {
            color: #64748b;
            font-size: 14px;
          }
          .footer {
            margin-top: 50px;
            padding-top: 20px;
            border-top: 2px solid #e2e8f0;
            color: #64748b;
            font-size: 12px;
          }
          @media print {
            body {
              margin: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>TPI Inspection Report Review Checklist</h1>
          <h2>Company: ${companyName || 'Not specified'}</h2>
        </div>
        
        <div class="meta-info">
          <div class="meta-item">
            <strong>Inspector:</strong> ${inspectorName || 'Not specified'}
          </div>
          <div class="meta-item">
            <strong>Inspection Date:</strong> ${inspectionDate || formattedDate}
          </div>
          <div class="meta-item">
            <strong>Report Status:</strong>
            <span class="status-badge" style="background-color: ${getStatusColor()}">
              ${getStatusLabel()}
            </span>
          </div>
          <div class="meta-item">
            <strong>Generated:</strong> ${formattedDate} at ${formattedTime}
          </div>
        </div>
        
        ${checklistSections.map((section, sectionIndex) => `
          <div class="checklist-section">
            <div class="section-title">${section.title}</div>
            <ul class="checklist-items">
              ${section.items.map((item, itemIndex) => {
                const key = `${sectionIndex}-${itemIndex}`;
                const isChecked = checkedItems[key] || false;
                return `
                  <li class="checklist-item ${isChecked ? 'checked' : ''}">
                    <div class="checkbox"></div>
                    <span class="item-text">${item}</span>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        `).join('')}
        
        <div class="summary">
          <h3>Checklist Summary</h3>
          <div class="stats">
            <div class="stat">
              <div class="stat-value">${completedCount}</div>
              <div class="stat-label">Completed Items</div>
            </div>
            <div class="stat">
              <div class="stat-value">${totalItems - completedCount}</div>
              <div class="stat-label">Pending Items</div>
            </div>
            <div class="stat">
              <div class="stat-value">${progress}%</div>
              <div class="stat-label">Progress</div>
            </div>
            <div class="stat">
              <div class="stat-value">${totalItems}</div>
              <div class="stat-label">Total Items</div>
            </div>
          </div>
          <div>
            <strong>Final Report Status:</strong>
            <span style="color: ${getStatusColor()}; font-weight: bold; margin-left: 10px;">
              ${getStatusLabel()}
            </span>
          </div>
        </div>
        
        
        
        <script>
          // Auto-print after a short delay
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    // Write content to new window
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Function to download PDF directly
  const downloadPDF = async () => {
    try {
      // Show loading state
      const downloadBtn = document.querySelector('.download-pdf-btn');
      if (downloadBtn) {
        const originalText = downloadBtn.innerHTML;
        downloadBtn.innerHTML = '<span>Generating PDF...</span>';
        downloadBtn.disabled = true;
      }

      // Get current date for filename
      const currentDate = new Date();
      const dateStr = currentDate.toISOString().split('T')[0];
      const timeStr = currentDate.getTime().toString().slice(-6);
      
      // Create PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add header
      pdf.setFontSize(20);
      pdf.setTextColor(30, 41, 59);
      pdf.text('TPI Inspection Report Review Checklist', 105, 20, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Company: ${companyName || 'Not specified'}`, 105, 30, { align: 'center' });
      
      // Add meta information
      pdf.setFontSize(10);
      pdf.setTextColor(30, 41, 59);
      let yPosition = 45;
      
      pdf.text(`Inspector: ${inspectorName || 'Not specified'}`, 20, yPosition);
      pdf.text(`Inspection Date: ${inspectionDate || dateStr}`, 20, yPosition + 7);
      pdf.text(`Report Status: ${getStatusLabel()}`, 20, yPosition + 14);
      pdf.text(`Generated: ${currentDate.toLocaleDateString()}`, 20, yPosition + 21);
      
      // Draw status indicator
      pdf.setFillColor(getStatusColor());
      pdf.roundedRect(85, yPosition + 10, 20, 10, 2, 2, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.text(getStatusLabel().toUpperCase(), 95, yPosition + 16, { align: 'center' });
      
      pdf.setTextColor(30, 41, 59);
      yPosition += 35;
      
      // Add checklist sections
      pdf.setFontSize(12);
      pdf.setTextColor(37, 99, 235);
      
      checklistSections.forEach((section, sectionIndex) => {
        // Check if we need a new page
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        // Add section title
        pdf.setFillColor(37, 99, 235);
        pdf.roundedRect(20, yPosition, 170, 10, 2, 2, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.text(section.title, 105, yPosition + 7, { align: 'center' });
        
        yPosition += 15;
        pdf.setTextColor(30, 41, 59);
        pdf.setFontSize(10);
        
        // Add checklist items
        section.items.forEach((item, itemIndex) => {
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          
          const key = `${sectionIndex}-${itemIndex}`;
          const isChecked = checkedItems[key] || false;
          
          // Draw checkbox
          pdf.setDrawColor(221, 221, 221);
          pdf.rect(20, yPosition - 3, 5, 5);
          
          if (isChecked) {
            pdf.setFillColor(16, 185, 129);
            pdf.rect(20, yPosition - 3, 5, 5, 'F');
            pdf.setTextColor(100, 116, 139);
            pdf.text(item, 30, yPosition, { 
              maxWidth: 160,
              align: 'left',
              renderingMode: 'fill'
            });
            // Add strikethrough
            pdf.setDrawColor(100, 116, 139);
            pdf.line(30, yPosition - 1, 30 + pdf.getTextWidth(item), yPosition - 1);
          } else {
            pdf.setTextColor(30, 41, 59);
            pdf.text(item, 30, yPosition, { 
              maxWidth: 160,
              align: 'left'
            });
          }
          
          yPosition += 7;
        });
        
        yPosition += 5; // Add spacing between sections
      });
      
      // Add summary section
      if (yPosition > 200) {
        pdf.addPage();
        yPosition = 20;
      }
      
      pdf.setFontSize(14);
      pdf.setTextColor(30, 41, 59);
      pdf.text('Checklist Summary', 20, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(10);
      
      // Summary stats
      const totalItems = checklistSections.reduce((sum, section) => sum + section.items.length, 0);
      const completedCount = Object.values(checkedItems).filter(Boolean).length;
      const pendingCount = totalItems - completedCount;
      
      const stats = [
        { label: 'Completed', value: completedCount, x: 30 },
        { label: 'Pending', value: pendingCount, x: 70 },
        { label: 'Progress', value: `${progress}%`, x: 110 },
        { label: 'Total', value: totalItems, x: 150 }
      ];
      
      stats.forEach(stat => {
        // Draw stat box
        pdf.setDrawColor(226, 232, 240);
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(stat.x, yPosition, 35, 20, 2, 2, 'FD');
        
        // Add value
        pdf.setFontSize(16);
        pdf.setTextColor(37, 99, 235);
        pdf.text(stat.value.toString(), stat.x + 17.5, yPosition + 12, { align: 'center' });
        
        // Add label
        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(stat.label, stat.x + 17.5, yPosition + 18, { align: 'center' });
      });
      
      yPosition += 30;
      
      // Final status
      pdf.setFontSize(10);
      pdf.setTextColor(30, 41, 59);
      pdf.text('Final Report Status:', 20, yPosition);
      pdf.setTextColor(getStatusColor());
      pdf.text(getStatusLabel(), 65, yPosition);
      
      // Add footer
      pdf.addPage();
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      
      // Save the PDF
      const filename = `TPI-Checklist-${companyName ? companyName.replace(/\s+/g, '-') : 'Report'}-${dateStr}.pdf`;
      pdf.save(filename);
      
      // Restore button state
      if (downloadBtn) {
        downloadBtn.innerHTML = '<span>Download PDF</span>';
        downloadBtn.disabled = false;
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
      
      // Restore button state on error
      const downloadBtn = document.querySelector('.download-pdf-btn');
      if (downloadBtn) {
        downloadBtn.innerHTML = '<span>Download PDF</span>';
        downloadBtn.disabled = false;
      }
    }
  };

  return (
    <div className="tpi-container">
      {/* Company & Inspector Details Section */}
      <div className="company-section">
        <div className="section-header">
          <Building size={24} className="section-icon" />
          <h2>Inspection Details</h2>
        </div>
        
        <div className="inspector-details">
          <div className="detail-group">
            <label htmlFor="inspectorName">Inspector Name:</label>
            <input
              type="text"
              id="inspectorName"
              value={inspectorName}
              onChange={(e) => setInspectorName(e.target.value)}
              placeholder="Enter inspector name"
              className="inspector-input"
            />
          </div>
          <div className="detail-group">
            <label htmlFor="companyName">Company Name:</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              className="inspector-input"
            />
          </div>
          <div className="detail-group">
            <label htmlFor="inspectionDate">Inspection Date:</label>
            <input
              type="date"
              id="inspectionDate"
              value={inspectionDate}
              onChange={(e) => setInspectionDate(e.target.value)}
              className="inspector-input"
            />
          </div>
          <div className="detail-group">
            <label>Report Status:</label>
            <div className="status-radio-group">
              {statusOptions.map((option) => (
                <div key={option.id} className="status-radio-item">
                  <input
                    type="radio"
                    id={option.id}
                    name="reportStatus"
                    value={option.value}
                    checked={reportStatus === option.value}
                    onChange={handleStatusChange}
                    className="status-radio-input"
                  />
                  <label htmlFor={option.id} className="status-radio-label">
                    <div 
                      className="status-indicator" 
                      style={{ 
                        backgroundColor: reportStatus === option.value ? getStatusColor() : '#e2e8f0',
                        borderColor: reportStatus === option.value ? getStatusColor() : '#e2e8f0'
                      }}
                    />
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="progress-section">
        <div className="progress-header">
          <div className="title-wrapper">
            <ClipboardList size={28} className="title-icon" />
            <h1 className="tpi-title">
              TPI Inspection Report Review Checklist
              {companyName && (
                <span className="company-subtitle"> - {companyName}</span>
              )}
            </h1>
          </div>
          <div className="header-actions">
            <button className="action-btn select-all" onClick={selectAll}>
              <CheckCircle size={18} />
              Select All
            </button>
            <button className="action-btn clear-all" onClick={clearAll}>
              <Circle size={18} />
              Clear All
            </button>
            <button className="action-btn print-btn" onClick={printReport}>
              <Printer size={18} />
              Print Report
            </button>
            <button className="action-btn download-pdf-btn" onClick={downloadPDF}>
              <Download size={18} />
              Download PDF
            </button>
          </div>
        </div>
        
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-text">
            <span>{progress}% Complete</span>
            <span>{Object.values(checkedItems).filter(Boolean).length} of {
              checklistSections.reduce((sum, section) => sum + section.items.length, 0)
            } items checked</span>
          </div>
        </div>
      </div>

      {/* Checklist Sections */}
      <div ref={componentRef} className="checklist-content">
        <div className="checklist-grid">
          {checklistSections.map((section, sectionIndex) => (
            <div 
              key={sectionIndex} 
              className="checklist-card"
            >
              <div className="card-header">
                <div className="section-icon">
                  {section.icon}
                </div>
                <h3 className="section-title">{section.title}</h3>
              </div>
              
              <ul className="section-items">
                {section.items.map((item, itemIndex) => {
                  const key = `${sectionIndex}-${itemIndex}`;
                  const isChecked = checkedItems[key] || false;
                  const isSelected = selectedItems[key] || false;
                  
                  return (
                    <li 
                      key={itemIndex} 
                      className={`item ${isChecked ? 'checked' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleCheck(sectionIndex, itemIndex)}
                      onKeyDown={(e) => handleKeyPress(e, sectionIndex, itemIndex)}
                      tabIndex={0}
                      role="checkbox"
                      aria-checked={isChecked}
                    >
                      <div className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          id={`checkbox-${key}`}
                          checked={isChecked}
                          onChange={(e) => handleCheckboxChange(e, sectionIndex, itemIndex)}
                          className="checkbox-input"
                          aria-label={`Check ${item}`}
                        />
                        <div className="custom-checkbox">
                          {isChecked ? (
                            <CheckCircle size={18} className="check-icon" />
                          ) : (
                            <div className="empty-checkbox" />
                          )}
                        </div>
                      </div>
                      <span className="item-text">{item}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Summary Section */}
        <div className="summary-section">
          <div className="summary-card">
            <h3>Checklist Summary</h3>
            <div className="summary-stats">
              <div className="stat">
                <div className="stat-value">
                  {Object.values(checkedItems).filter(Boolean).length}
                </div>
                <div className="stat-label">Completed</div>
              </div>
              <div className="stat">
                <div className="stat-value">
                  {Object.values(checkedItems).filter(item => !item).length}
                </div>
                <div className="stat-label">Pending</div>
              </div>
              <div className="stat">
                <div className="stat-value">{progress}%</div>
                <div className="stat-label">Progress</div>
              </div>
              <div className="stat">
                <div className="stat-value">
                  {companyName ? companyName.split(' ')[0] : 'N/A'}
                </div>
                <div className="stat-label">Company</div>
              </div>
            </div>
            <div className="report-status-summary">
              <strong>Final Report Status:</strong>
              <span className="final-status" style={{ color: getStatusColor() }}>
                {getStatusLabel()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Completion Section */}
      {completedAll && (
        <div className="completion-banner">
          <div className="completion-content">
            <div className="celebration">🎉</div>
            <h3>Checklist Complete!</h3>
            <p>All inspection items have been reviewed and verified.</p>
            <div className="completion-info">
              <p><strong>Company:</strong> {companyName || 'Not specified'}</p>
              <p><strong>Inspector:</strong> {inspectorName || 'Not specified'}</p>
              <p><strong>Status:</strong> <span style={{ color: getStatusColor() }}>{getStatusLabel()}</span></p>
            </div>
            <div className="completion-actions">
              <button className="reset-btn" onClick={resetChecklist}>
                Start New Review
              </button>
              <button className="print-complete-btn" onClick={printReport}>
                <Printer size={18} />
                Print Report
              </button>
              <button className="download-complete-btn" onClick={downloadPDF}>
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions">
        <div className="instruction-item">
          <div className="instruction-dot completed" />
          <span>Click items or use spacebar to mark as complete</span>
        </div>
        <div className="instruction-item">
          <div className="instruction-dot pending" />
          <span>Fill in inspector details before generating reports</span>
        </div>
        <div className="instruction-item">
          <div className="instruction-dot print" />
          <span>Use Print Report for print dialog, Download PDF for direct download</span>
        </div>
      </div>
    </div>
  );
};

export default ReportReviewChecklist;